import type { Namespace } from '../types';

// R-227. DE drafted — queued for native review with the rest.
// "Firmware" and the board-model strings stay invariant (they are what the
// hardware reports).
export const firmware = {
  title: { de: 'Firmware', en: 'Firmware' },
  desc: {
    de: 'Firmware-Images hochladen und an die Controller der Farm ausrollen — mit Verlauf und Begründung.',
    en: 'Upload firmware images and roll them out to the farm’s controllers — with a history and a stated reason.',
  },

  statImages: { de: 'Images', en: 'Images' },
  statLive: { de: 'Läuft gerade', en: 'Running now' },
  statHistory: { de: 'Ausrollungen gesamt', en: 'Rollouts total' },

  statTotalBoards: { de: 'Boards gesamt', en: 'Total boards' },
  statSelected: { de: 'Ausgewählt', en: 'Selected' },
  statWillUpdate: { de: 'Wird aktualisiert', en: 'Will update' },
  statBusesAffected: { de: 'Betroffene Busse', en: 'Buses affected' },

  sectionLive: { de: 'Läuft gerade', en: 'Running now' },
  liveHint: {
    de: 'Kinder zuerst, der Parent zuletzt — sein Neustart unterbricht kurz die serielle Verbindung des Raums.',
    en: 'Children first, the parent last — its reboot briefly interrupts the room’s serial link.',
  },
  sectionImages: { de: 'Hochgeladene Images', en: 'Uploaded images' },
  imagesHint: {
    de: 'Version und Board-Modell werden aus der Binärdatei GELESEN, nicht eingegeben — ein Build kann nicht falsch beschriftet werden.',
    en: 'The version and board model are READ from the binary, never typed — a build cannot be mislabelled.',
  },
  sectionHistory: { de: 'Verlauf', en: 'History' },
  noHistory: { de: 'Noch keine abgeschlossenen Ausrollungen.', en: 'No finished rollouts yet.' },
  noImages: { de: 'Noch keine Images', en: 'No images yet' },
  noImagesMsg: {
    de: 'Laden Sie eine `.bin` aus dem Firmware-Build hoch.',
    en: 'Upload a `.bin` from the firmware build.',
  },

  colVersion: { de: 'Version', en: 'Version' },
  colMessage: { de: 'Meldung', en: 'Message' },
  colEvent: { de: 'Ereignis', en: 'Event' },
  colTime: { de: 'Zeit', en: 'Time' },
  colProgress: { de: 'Fortschritt', en: 'Progress' },
  colModel: { de: 'Board', en: 'Board' },
  colSize: { de: 'Größe', en: 'Size' },
  colUploaded: { de: 'Hochgeladen', en: 'Uploaded' },
  colNotes: { de: 'Notiz', en: 'Note' },

  upload: { de: 'Image hochladen', en: 'Upload image' },
  uploadTitle: { de: 'Firmware-Image hochladen', en: 'Upload a firmware image' },
  uploadHint: {
    de: 'Die Datei entscheidet über Version und Board-Modell — beide stehen in der Binärdatei. Ein Image, das nicht eindeutig ist, wird abgelehnt.',
    en: 'The file decides the version and board model — both are embedded in the binary. An image that is not unambiguous is refused.',
  },
  // The build drops THREE .bin files next to each other and only one is the
  // application image. Naming the exact paths is what stops someone uploading
  // bootloader.bin — which is a real file, of a plausible size, and wrong.
  whichFile: { de: 'Welche Datei?', en: 'Which file?' },
  whichFileHint: {
    de: 'Nach `pio run` liegt sie im Build-Verzeichnis. Es ist immer `firmware.bin` — NICHT `bootloader.bin` oder `partitions.bin`, die daneben liegen.',
    en: 'After `pio run` it is in the build directory. It is always `firmware.bin` — NOT `bootloader.bin` or `partitions.bin`, which sit beside it.',
  },
  exampleGrow: { de: 'Grow Room', en: 'Grow room' },
  examplePlumb: { de: 'Plumbing Room', en: 'Plumbing room' },
  exampleSize: { de: 'typisch ~330–360 KiB', en: 'typically ~330–360 KiB' },
  uploaded: { de: '{model} {version} hochgeladen', en: '{model} {version} uploaded' },
  notes: { de: 'Notiz', en: 'Note' },
  notesHint: { de: 'Was ist in diesem Build?', en: 'What is in this build?' },
  nextHint: {
    de: 'Nächste Version für den nächsten Build — in `config.h` setzen, dann bauen:',
    en: 'Next version for the next build — set this in `config.h`, then build:',
  },

  push: { de: 'Ausrollen', en: 'Push' },
  pushTitle: { de: '{version} ausrollen ({model})', en: 'Push {version} ({model})' },
  colPick: { de: 'Wahl', en: 'Pick' },
  colBus: { de: 'Bus', en: 'Bus' },
  colPlanStatus: { de: 'Stand', en: 'Status' },
  planTitle: { de: 'Ausrollplan', en: 'Rollout plan' },
  busReady: { de: 'Bereit', en: 'Ready to queue' },
  reasonPh: {
    de: 'z. B. Relais-Fehler behoben — Build 0.3.24 auf Rack 2',
    en: 'e.g. Relay fault fixed — build 0.3.24 onto rack 2',
  },
  viewRollouts: { de: 'Ausrollungen ansehen', en: 'View rollouts' },
  confirmPushTitle: {
    de: 'Image {version} auf {buses} Bus(se) ausrollen?',
    en: 'Push {version} onto {buses} bus(es)?',
  },
  confirmPushMsg: {
    de: '{boards} Board(s) werden geflasht. Jeder Bus wird als eigener Ausrollauftrag eingereiht; ein Parent zu flashen unterbricht die Raumsteuerung für einige Sekunden. Das lässt sich nicht zurücknehmen.',
    en: '{boards} board(s) will be flashed. Each bus is queued as its own rollout, and flashing a parent interrupts room control for a few seconds. This cannot be taken back.',
  },
  confirmPushForce: {
    de: 'Die Prüfungen von Version UND Modell sind übergangen. Ein falsches Image kann ein Board unbrauchbar machen. Das lässt sich nicht zurücknehmen.',
    en: 'The version AND model checks are overridden. The wrong image can leave a board unusable. This cannot be taken back.',
  },
  toastPushQueued: { de: '{n} Bus(se) eingereiht', en: '{n} bus(es) queued' },
  toastPushFailed: { de: '{n} Bus(se) fehlgeschlagen', en: '{n} bus(es) failed' },
  startPush: { de: 'Ausrollen starten', en: 'Start push' },
  queued: { de: 'Ausrollung eingereiht', en: 'Rollout queued' },
  pushWarning: {
    de: 'Beim Flashen des Parents ist die Raumsteuerung für einige Sekunden unterbrochen. Boards mit anderem Modell oder bereits dieser Version werden übersprungen.',
    en: 'Flashing the parent interrupts room control for a few seconds. Boards on another model, or already on this version, are skipped.',
  },
  parent: { de: 'Parent-Controller', en: 'Parent controller' },
  parentHint: {
    de: 'Die Bridge dieses Parents führt die Ausrollung durch.',
    en: 'This parent’s bridge runs the rollout.',
  },
  targets: { de: 'Ziele', en: 'Targets' },
  targetsAll: {
    de: 'Nichts ausgewählt — alle Boards an diesem Parent.',
    en: 'Nothing selected — every board on this parent.',
  },
  targetsSome: { de: 'Nur die ausgewählten Boards.', en: 'Only the selected boards.' },
  searchControllers: { de: 'Controller suchen…', en: 'Search controllers…' },
  selected: { de: 'Ausgewählt', en: 'Selected' },
  roleParent: { de: 'Parent', en: 'Parent' },
  notOnAnyBus: {
    de: 'Keinem Bus zugeordnet und daher nicht flashbar — zuerst einem Ort zuweisen:',
    en: 'On no bus, so not flashable — assign to a location first:',
  },
  channels: { de: '{n} Kanäle', en: '{n} channels' },

  reason: { de: 'Grund', en: 'Reason' },
  reasonHint: {
    de: 'Warum wird diese Firmware ausgerollt? Pflichtfeld — es ist das Einzige, was eine spätere Analyse nicht rekonstruieren kann.',
    en: 'Why is this firmware being pushed? Required — it is the one thing a later review cannot reconstruct.',
  },
  force: { de: 'Prüfungen übergehen (force)', en: 'Override the checks (force)' },
  forceHint: {
    de: 'Überschreibt die Versions- UND Modellprüfung der Bridge. Damit kann ein Grow-Image auf einem Plumbing-Board landen — genau der Fehler, der einen Phantom-Controller erzeugt hat.',
    en: 'Overrides the bridge’s version AND model checks. That allows a grow image onto a plumbing board — precisely the mistake that produced a phantom controller.',
  },
  forced: { de: 'erzwungen', en: 'forced' },

  progress: {
    de: '{done} aktualisiert · {skipped} übersprungen · {failed} fehlgeschlagen · von {total}',
    en: '{done} updated · {skipped} skipped · {failed} failed · of {total}',
  },
  // Said separately from the status: "die Ausrollung meldet fertig" und "das Board
  // meldet die neue Version" sind zwei Aussagen, und nur die zweite ist ein Beleg.
  notConfirmed: { de: '⚠ meldet andere Version', en: '⚠ reports another version' },
  awaitingReport: { de: 'wartet auf Rückmeldung', en: 'awaiting report' },

  role_parent: { de: 'Parent', en: 'Parent' },
  role_child: { de: 'Kind', en: 'Child' },

  status_queued: { de: 'eingereiht', en: 'queued' },
  status_publishing: { de: 'wird gesendet', en: 'publishing' },
  status_running: { de: 'läuft', en: 'running' },
  status_complete: { de: 'abgeschlossen', en: 'complete' },
  status_failed: { de: 'fehlgeschlagen', en: 'failed' },
  status_aborted: { de: 'abgebrochen', en: 'aborted' },

  tstatus_pending: { de: 'wartet', en: 'pending' },
  tstatus_streaming: { de: 'überträgt', en: 'streaming' },
  tstatus_done: { de: 'fertig', en: 'done' },
  tstatus_skipped: { de: 'übersprungen', en: 'skipped' },
  tstatus_failed: { de: 'fehlgeschlagen', en: 'failed' },

  loadError: { de: 'Firmware konnte nicht geladen werden', en: 'Couldn’t load firmware' },
  loadErrorMsg: {
    de: 'Der gateway-local-Dienst ist nicht erreichbar. Bitte gleich erneut versuchen.',
    en: 'The gateway-local service is unreachable. Retry shortly.',
  },

  // ── tabs ───────────────────────────────────────────────────────────────────
  tabImages: { de: 'Images', en: 'Images' },
  tabActive: { de: 'Aktiv', en: 'Active' },
  tabHistory: { de: 'Verlauf', en: 'History' },

  // ── active / progress ──────────────────────────────────────────────────────
  noActive: { de: 'Gerade läuft keine Ausrollung.', en: 'No rollout is running.' },
  noActiveHint: {
    de: 'Eine laufende Ausrollung erscheint hier mit jedem Board einzeln.',
    en: 'A running rollout appears here with each board listed individually.',
  },
  queuePos: { de: 'Warteschlange', en: 'Queue' },
  etaLabel: { de: 'ca. noch', en: 'approx. left' },
  etaEstimate: {
    de: 'Schätzung aus der gemessenen Busrate (~544 B/s) — keine Zusage.',
    en: 'Estimated from the measured bus rate (~544 B/s) — not a promise.',
  },
  elapsed: { de: 'Laufzeit', en: 'Elapsed' },
  versionMove: { de: 'Version', en: 'Version' },

  // ── history ────────────────────────────────────────────────────────────────
  filterStatus: { de: 'Status', en: 'Status' },
  filterModel: { de: 'Board-Modell', en: 'Board model' },
  filterVersion: { de: 'Version', en: 'Version' },
  filterMac: { de: 'Board (MAC)', en: 'Board (MAC)' },
  filterReason: { de: 'Begründung enthält', en: 'Reason contains' },
  filterFrom: { de: 'Von', en: 'From' },
  filterTo: { de: 'Bis', en: 'To' },
  filterClear: { de: 'Filter zurücksetzen', en: 'Clear filters' },
  filterAllStatuses: { de: 'Alle Status', en: 'All statuses' },
  filterAllModels: { de: 'Alle Modelle', en: 'All models' },
  colReason: { de: 'Begründung', en: 'Reason' },
  noImageMatches: { de: 'Kein Image passt zu Suche/Filter.', en: 'No image matches the search / filter.' },
  searchImages: { de: 'Version oder Notiz suchen…', en: 'Search version or note…' },
  chooseFile: { de: 'Datei wählen', en: 'Choose file' },
  noFileChosen: { de: 'Noch keine .bin-Datei gewählt', en: 'No .bin file chosen yet' },
  notesPlaceholder: { de: 'z. B. Relais-Entprellung behoben', en: 'e.g. Fixed relay debounce' },
  crc32: { de: 'CRC32', en: 'CRC32' },
  sha256: { de: 'SHA-256', en: 'SHA-256' },
  imageId: { de: 'Image-ID', en: 'Image ID' },
  loadMore: { de: 'Mehr laden', en: 'Load more' },
  noMatches: { de: 'Keine Ausrollung passt zu diesen Filtern.', en: 'No rollout matches these filters.' },
  colWhen: { de: 'Wann', en: 'When' },
  colOutcome: { de: 'Ergebnis', en: 'Outcome' },
  colBoards: { de: 'Boards', en: 'Boards' },
  countsLabel: {
    de: '{done} ok · {failed} fehlgeschlagen · {skipped} übersprungen',
    en: '{done} ok · {failed} failed · {skipped} skipped',
  },
  detailTitle: { de: 'Ausrollung', en: 'Rollout' },
  tabDetailBoards: { de: 'Boards', en: 'Boards' },
  tabDetailEvents: { de: 'Ereignisprotokoll', en: 'Event log' },
  eventsHint: {
    de: 'Rohprotokoll der Bridge, unverändert — die Diagnose-Aufzeichnung.',
    en: 'The bridge’s raw log, verbatim — the diagnosis record.',
  },
  noEvents: { de: 'Keine Ereignisse aufgezeichnet.', en: 'No events recorded.' },

  // ── upload help ────────────────────────────────────────────────────────────
  uploadHelp: {
    de: 'Bauen: cd code/firmware/<growroom|plumbing> && pio run. Die Datei ist .pio/build/<env>/firmware.bin (~330 KB). Version und Board-Modell werden AUS DEN BYTES gelesen — es gibt bewusst keine Felder dafür, denn ein Feld, das man tippen kann, kann man falsch tippen. Die Version stammt aus FW_MAJOR/MINOR/PATCH in config.h: dort erhöhen und neu bauen. Erlaubt sind 1 KiB bis 3,25 MiB; ein nicht identifizierbares Image wird abgelehnt, nicht geraten.',
    en: 'Build: cd code/firmware/<growroom|plumbing> && pio run. The file is .pio/build/<env>/firmware.bin (~330 KB). Version and board model are read FROM THE BYTES — there are deliberately no fields for them, because a field you can type is a field you can mistype. The version comes from FW_MAJOR/MINOR/PATCH in config.h: bump it there and rebuild. Accepted size is 1 KiB to 3.25 MiB; an image that cannot be identified is refused rather than guessed.',
  },

  // ── targeting ──────────────────────────────────────────────────────────────
  targetAll: { de: 'Alle', en: 'All' },
  targetChildren: { de: 'Nur Kinder', en: 'Children only' },
  targetParent: { de: 'Nur Parent', en: 'Parent only' },
  targetSpecific: { de: 'Auswahl', en: 'Specific' },
  selectAll: { de: 'Alle auswählen', en: 'Select all' },
  selectNone: { de: 'Keine', en: 'None' },
  selectInvert: { de: 'Umkehren', en: 'Invert' },
  searchBoards: { de: 'MAC, Name oder Version suchen…', en: 'Search MAC, name or version…' },
  preflightTitle: { de: 'Vorabprüfung', en: 'Preflight' },
  preflightSummary: {
    de: '{update} werden aktualisiert · {skip} übersprungen · {offline} nicht erreichbar',
    en: '{update} will update · {skip} skipped · {offline} not reporting',
  },
  preflightNoop: {
    de: 'Nichts würde passieren — jedes gewählte Board würde übersprungen.',
    en: 'Nothing would happen — every selected board would be skipped.',
  },
  preflightHint: {
    de: 'Dieselben Regeln, die die Bridge anwendet: gleiche Version wird übersprungen, fremdes Modell wird übersprungen, ein nicht meldendes Board kann nicht beschrieben werden.',
    en: 'The same rules the bridge applies: same version is skipped, a different model is skipped, and a board that is not reporting cannot be written to.',
  },
  currentVersion: { de: 'Aktuell', en: 'Currently' },
  neverReported: { de: 'nie gemeldet', en: 'never reported' },

  // R-230 — the push PAGE: a searchable board list, sized by the farm.
  pushPageHint: {
    de: 'Boards auswählen, die dieses Image erhalten sollen. Ein Bus (Parent + Kinder) wird als eine Ausrollung eingereiht.',
    en: 'Pick the boards that should take this image. Each bus (a parent and its children) is queued as one rollout.',
  },
  pushMissingTitle: { de: 'Image nicht gefunden', en: 'Image not found' },
  pushMissingMsg: {
    de: 'Dieses Firmware-Image existiert nicht mehr — vielleicht wurde es gelöscht.',
    en: 'That firmware image no longer exists — it may have been deleted.',
  },
  backToFirmware: { de: 'Zurück zu Firmware', en: 'Back to Firmware' },
  buses: { de: 'Busse', en: 'Buses' },
  allBuses: { de: 'Alle Busse', en: 'All buses' },
  nOfM: { de: '{n} von {m} gewählt', en: '{n} of {m} selected' },
  busWrongModelShort: { de: 'anderes Board-Modell', en: 'different board model' },
  filterNeeds: { de: 'Braucht Update', en: 'Needs update' },
  filterEligible: { de: 'Passend', en: 'Eligible' },
  filterAll: { de: 'Alle', en: 'All' },
  selectLabel: { de: 'Auswählen:', en: 'Select:' },
  selectAllShown: { de: 'Alle sichtbaren', en: 'All shown' },
  noBoardsMatch: {
    de: 'Keine Boards passen. Mit „Alle" auch die Busse anderer Modelle zeigen.',
    en: 'No boards match. Switch to “All” to include buses running another model.',
  },
  allCurrent: {
    de: 'Nichts zu tun — jedes passende Board meldet bereits {version}.',
    en: 'Nothing to do — every eligible board already reports {version}.',
  },
  allCurrentHint: {
    de: 'Mit „Passend" die Boards sehen, die bestätigen, dass sie aktuell sind.',
    en: 'Switch to “Eligible” to see the boards confirming they are up to date.',
  },
  colBoard: { de: 'Board', en: 'Board' },
  colLocation: { de: 'Ort', en: 'Location' },
  colRole: { de: 'Rolle', en: 'Role' },
  colWill: { de: 'Ergebnis', en: 'Will' },
  // Short chips for a dense list. The full sentence from the preflight rides
  // along as the row's title, so nothing is lost — it just is not clipped.
  willUpdate: { de: 'aktualisieren', en: 'will update' },
  willSkipVersion: { de: 'schon aktuell', en: 'already current' },
  willSkipModel: { de: 'falsches Modell', en: 'wrong model' },
  willSkipOffline: { de: 'meldet nicht', en: 'not reporting' },
  pushSelection: {
    de: '{boards} Board(s) auf {buses} Bus(sen) — {update} werden aktualisiert.',
    en: '{boards} board(s) on {buses} bus(es) — {update} will update.',
  },

  // R-228 — one card per RS485 bus.
  targetSkip: { de: 'Überspringen', en: 'Skip' },
  roleChild: { de: 'Kind', en: 'Child' },
  // Two keys because the i18n layer interpolates and does not pluralise; a bus
  // with one board is the normal case for a plumbing room with no nodes mapped.
  busBoards: { de: '{n} Boards', en: '{n} boards' },
  busBoardsOne: { de: '1 Board', en: '1 board' },
  busQueued: { de: 'Eingereiht', en: 'Queued' },
  busFailed: { de: 'Fehlgeschlagen', en: 'Failed' },
  busWrongModel: {
    de: 'Dieser Bus fährt {busModel}; das Image ist für {imageModel}. Die Bridge überspringt jedes Board — außer mit force.',
    en: 'This bus runs {busModel}; the image is for {imageModel}. The bridge will skip every board unless you force it.',
  },
  parentLast: {
    de: 'Parent zuletzt — sein Neustart trennt kurz die Raumsteuerung.',
    en: 'Parent goes last — its reboot briefly drops room control.',
  },
  busesHint: {
    de: 'Ein Bus = ein Parent + seine Kinder = eine Ausrollung. Jeder Bus wird einzeln eingereiht.',
    en: 'One bus = a parent and its children = one rollout. Each bus is queued separately.',
  },
  pushSummary: {
    de: '{buses} Bus(se), {update} Board(s) werden aktualisiert.',
    en: '{buses} bus(es), {update} board(s) will update.',
  },
  pushNothing: {
    de: 'Nichts ausgewählt — wähle für mindestens einen Bus ein Ziel.',
    en: 'Nothing selected — choose a target on at least one bus.',
  },
  retryFailed: { de: 'Fehlgeschlagene wiederholen', en: 'Retry failed' },
  pushPartial: {
    de: '{ok} eingereiht, {failed} fehlgeschlagen. Bereits eingereihte Busse werden nicht erneut gesendet.',
    en: '{ok} queued, {failed} failed. Buses already queued are not sent again.',
  },
  declareVersion: { de: 'Version dieses Builds', en: 'Version of this build' },
  declareVersionHelp: {
    de: 'Vorbelegt mit der nächsten Patch-Version. Diese Angabe wird gegen die Bytes GEPRÜFT, nicht übernommen: das Board meldet, was in config.h einkompiliert wurde. Stimmen sie nicht überein, wird der Upload abgelehnt — genau das fängt den vergessenen Versionssprung ab.',
    en: 'Prefilled with the next patch version. This is CHECKED against the bytes, never substituted for them: the board reports whatever config.h compiled in. If they disagree the upload is refused — which is exactly what catches a forgotten version bump.',
  },
  vMajor: { de: 'major', en: 'major' },
  vMinor: { de: 'minor', en: 'minor' },
  vPatch: { de: 'patch', en: 'patch' },
  declaredIs: { de: 'Angegeben:', en: 'Declaring:' },
  declaredCheck: {
    de: 'muss exakt der im Image einkompilierten Version entsprechen.',
    en: 'must match the version compiled into the image exactly.',
  },
} as const satisfies Namespace;
